import { Container, Graphics } from "../../../vendor/pixi.min.mjs";
import { ObjectPool } from "../../systems/ObjectPool.js";
import { FroggerVehicle } from "./FroggerVehicle.js";
import { FROGGER_CONFIG } from "./froggerConfig.js";
import { drawBush, drawTree } from "../../ui/Decor.js";

const {
  CELL_SIZE,
  COLS,
  VEHICLE_TYPES,
  SPEED_RANGE,
  SPAWN_RATE_RANGE,
  SPAWN_JITTER,
  MIN_VEHICLE_GAP,
  SPAWN_EDGE_CLEARANCE,
  MAX_VEHICLES_PER_ROW,
  INITIAL_VEHICLES_RANGE,
  SAFE_INTERVAL_RANGE,
  COLOR_SAFE_ZONE,
  COLOR_ROAD,
  COLOR_LANE_LINE,
  SPEED_INCREASE_PER_10_ROWS,
  MIN_SPAWN_RATE,
  LANE_CHANGE_CHANCE,
  DENSITY_PROFILES,
  START_SAFE_ROWS = 1,
} = FROGGER_CONFIG;

const GRID_W = COLS * CELL_SIZE;

/**
 * Safe-row decoration variants. Sizes are fractions of CELL_SIZE.
 * `weight` biases the random pick (higher = more common). Small ground-cover
 * items (grass, flowers) dominate; trees fill the middle; benches and lamps
 * are rare. Benches additionally have a hard per-row cap (see SAFE_DECOR_MAX_*).
 * Each variant gets a procedural fallback only for tree/pinetree; the rest
 * rely on the user-uploaded sprite (PNGs ship in assets/frogger/).
 */
const SAFE_DECOR_KINDS = [
  {
    id: "grass",
    slot: "frogger.decor.grass",
    w: 0.92,
    h: 0.55,
    weight: 6,
    fallback: null,
  },
  {
    id: "flowers",
    slot: "frogger.decor.flowers",
    w: 0.88,
    h: 0.65,
    weight: 5,
    fallback: null,
  },
  {
    id: "tree",
    slot: "frogger.decor.tree",
    w: 0.82,
    h: 0.92,
    weight: 7,
    fallback: "tree",
  },
  {
    id: "pinetree",
    slot: "frogger.decor.pinetree",
    w: 0.7,
    h: 0.95,
    weight: 7,
    fallback: "tree",
  },
  {
    id: "lamp",
    slot: "frogger.decor.lamp",
    w: 0.45,
    h: 0.95,
    weight: 2,
    fallback: null,
  },
  {
    id: "bench",
    slot: "frogger.decor.bench",
    w: 1.25,
    h: 0.55,
    weight: 1,
    fallback: null,
  },
];
const SAFE_DECOR_WEIGHT_SUM = SAFE_DECOR_KINDS.reduce(
  (s, k) => s + k.weight,
  0,
);
/** Which kinds are eligible to randomly grow tall and spill onto the row above. */
const SAFE_DECOR_TALL_KINDS = new Set(["tree", "pinetree"]);
/** Probability (0..1) that an eligible tree is upgraded to a tall variant. */
const SAFE_DECOR_TALL_PROB = 0.3;
/** Tall variant size multiplier — picked uniformly in this range. */
const SAFE_DECOR_TALL_MUL = [1.55, 1.95];
/** Hard per-row cap on the more sparse kinds. */
const SAFE_DECOR_MAX_PER_ROW = { bench: 1, lamp: 1 };
/** Min/max decorations to place on each safe row (varies per row by seed). */
const SAFE_DECOR_COUNT_MIN = 20;
const SAFE_DECOR_COUNT_MAX = 30;
/** All decorations sit so their bottom edge lines up at this fraction of the cell. */
const SAFE_DECOR_BASE_FRACTION = 0.92;

/**
 * @typedef {Object} RowData
 * @property {'safe'|'road'} type
 * @property {Graphics} bgGfx
 * @property {Object} [laneDef]
 * @property {number} [spawnTimer]
 */

/**
 * Dynamically generates and manages rows for the endless frogger.
 * Rows are created on-demand as they enter the visible area and
 * cleaned up when they leave.
 */
export class RowManager {
  /** @type {Container} Root — add this to the scene */
  container = new Container();
  _bgLayer = new Container();
  _vehicleLayer = new Container();
  /**
   * Layer for oversized tree canopies that "spill" up onto the road
   * above. Sits ON TOP of the vehicle layer so cars visually pass behind
   * the canopy. Purely cosmetic — no hitbox.
   */
  _overhangLayer = new Container();

  /** @type {Map<number, RowData>} worldRow → row data */
  _rows = new Map();

  /** @type {ObjectPool} */
  _pool = null;

  /** Tracks generation state */
  _highestGenerated = -1;
  _nextSafeIn = 0;

  /** Caches the row type so regenerated rows keep the same type */
  _rowTypeCache = new Map();

  _playerExcludeX = 0;
  _playerExcludeW = 0;

  _originX = 0;
  _screenWidth = 0;

  /** @type {((worldRow: number, type: string) => void)|null} */
  _onRowGenerated = null;

  _goalRow = Infinity;
  _themeManager = null;

  /**
   * @param {number} originX — left edge of the grid in world X
   * @param {number} screenWidth
   * @param {((worldRow: number, type: string) => void)|null} [onRowGenerated]
   * @param {number} [goalRow] — for fixed mode, rows >= goalRow-2 are forced safe
   */
  _speedMultiplier = 1.0;
  _liveSpeedFactor = 1.0;
  _laneChangingEnabled = false;
  _density = {
    spawnRateDiv: 1.0,
    maxVehicleMul: 1.0,
    initialMul: 1.0,
    gapMul: 1.0,
  };

  constructor(
    originX,
    screenWidth,
    onRowGenerated = null,
    goalRow = Infinity,
    themeManager = null,
  ) {
    this._originX = originX;
    this._screenWidth = screenWidth;
    this._onRowGenerated = onRowGenerated;
    this._goalRow = goalRow;
    this._themeManager = themeManager;
    this._pool = new ObjectPool(
      () => new FroggerVehicle(this._themeManager),
      30,
    );
    this._nextSafeIn = 0;
    this.container.addChild(this._bgLayer);
    this.container.addChild(this._vehicleLayer);
    this.container.addChild(this._overhangLayer);
  }

  setSpeedMultiplier(mul) {
    this._speedMultiplier = mul;
  }

  enableLaneChanging(enabled) {
    this._laneChangingEnabled = enabled;
  }

  setLiveSpeedFactor(factor) {
    this._liveSpeedFactor = factor;
  }

  setDensityProfile(key) {
    const profile = DENSITY_PROFILES[key] ?? DENSITY_PROFILES.normal;
    this._density = {
      spawnRateDiv: profile.spawnRateDiv ?? 1.0,
      maxVehicleMul: profile.maxVehicleMul ?? 1.0,
      initialMul: profile.initialMul ?? 1.0,
      gapMul: profile.gapMul ?? 1.0,
    };
  }

  setPlayerExclusion(x, w) {
    this._playerExcludeX = x;
    this._playerExcludeW = w;
  }

  /**
   * Ensure all rows in [lowRow, highRow] are generated.
   * Generates sequentially so safe-zone spacing stays consistent.
   */
  /**
   * Ensure all rows in [lowRow, highRow] exist.
   * Generates new rows beyond _highestGenerated sequentially (so safe-zone
   * spacing stays consistent), and re-generates any previously cleaned-up rows.
   *
   * NOTE: This method enforces a hard ceiling at `_goalRow`. Nothing past
   * the museum row is ever created, no matter what the camera asks for.
   */
  ensureRows(lowRow, highRow) {
    if (Number.isFinite(this._goalRow)) {
      highRow = Math.min(highRow, this._goalRow);
    }
    if (highRow < 0) return;

    // First: extend forward sequentially to keep safe-zone pattern intact
    if (highRow > this._highestGenerated) {
      for (let r = this._highestGenerated + 1; r <= highRow; r++) {
        if (!this._rows.has(r)) this._generateRow(r);
      }
      this._highestGenerated = highRow;
    }

    // Second: fill any gaps (rows that were cleaned up and need rebuilding)
    for (let r = Math.max(0, lowRow); r <= highRow; r++) {
      if (!this._rows.has(r)) this._generateRow(r);
    }
  }

  /**
   * Update vehicles in visible rows. Spawn new ones (with gap + cap checks),
   * move existing, recycle off-screen.
   */
  updateVisibleRows(lowRow, highRow, delta) {
    for (let r = Math.max(0, lowRow); r <= highRow; r++) {
      const row = this._rows.get(r);
      if (!row || row.type !== "road") continue;
      // Rows with no laneDef are inert "decoration only" road rows (e.g. the
      // win row beneath the finish building) — no traffic to update or spawn.
      if (!row.laneDef) continue;

      const laneDef = row.laneDef;
      const speedMul =
        (1 + Math.floor(r / 10) * SPEED_INCREASE_PER_10_ROWS) *
        this._speedMultiplier;

      if (row.vehicles) {
        for (let i = row.vehicles.length - 1; i >= 0; i--) {
          const v = row.vehicles[i];
          v.update(delta, this._liveSpeedFactor);
          if (v.isOffScreen(this._screenWidth)) {
            this._vehicleLayer.removeChild(v.display);
            row.vehicles.splice(i, 1);
            this._pool.release(v);
            continue;
          }
          if (
            this._laneChangingEnabled &&
            v.canDrift &&
            Math.random() < LANE_CHANGE_CHANCE
          ) {
            this._tryLaneChange(v, r, row);
          }
        }
        this._resolveOverlaps(row.vehicles);
      }

      row.spawnTimer += delta;
      const rate = Math.max(
        MIN_SPAWN_RATE,
        Math.round(laneDef.spawnRate / this._density.spawnRateDiv),
      );
      const maxVeh = Math.max(
        1,
        Math.round(MAX_VEHICLES_PER_ROW * this._density.maxVehicleMul),
      );

      if (row.spawnTimer >= rate) {
        row.spawnTimer = -this._randRange(0, SPAWN_JITTER);

        if (
          row.vehicles.length < maxVeh &&
          this._isSpawnEdgeClear(row, laneDef)
        ) {
          this._spawnVehicle(r, laneDef, speedMul);
        }
      }
    }
  }

  /**
   * Check that no vehicle is too close to the spawn edge.
   * Prevents new vehicles from appearing right next to existing ones.
   */
  _isSpawnEdgeClear(rowData, laneDef) {
    if (!rowData.vehicles || rowData.vehicles.length === 0) return true;

    const clearance = SPAWN_EDGE_CLEARANCE * this._density.gapMul;
    for (const v of rowData.vehicles) {
      if (laneDef.direction > 0) {
        // Spawns from left edge
        if (v.x - this._originX < clearance) return false;
      } else {
        // Spawns from right edge
        const rightEdge = this._originX + GRID_W;
        if (rightEdge - (v.x + v.hitbox.width) < clearance) return false;
      }
    }
    return true;
  }

  /**
   * Remove all vehicles on a given row (used by bomb ability).
   *
   * Returns the centre point of each vehicle that was destroyed, in world
   * coordinates, so the caller can spawn explosion FX at the exact spots
   * the cars disappeared from. Empty array if the row had no traffic
   * (safe lane, off-screen row, etc.).
   *
   * @param {number} worldRow
   * @returns {Array<{x: number, y: number}>}
   */
  clearRow(worldRow) {
    const row = this._rows.get(worldRow);
    if (!row?.vehicles) return [];
    const positions = [];
    for (const v of row.vehicles) {
      const r = v.getHitRect();
      positions.push({ x: r.x + r.w / 2, y: r.y + r.h / 2 });
      this._vehicleLayer.removeChild(v.display);
      this._pool.release(v);
    }
    row.vehicles.length = 0;
    return positions;
  }

  /**
   * Remove any vehicle on the given row that overlaps with a rect.
   * Prevents phantom kills from vehicles spawned on top of the player
   * when rows are regenerated.
   */
  clearOverlapping(rect, worldRow) {
    const row = this._rows.get(worldRow);
    if (!row?.vehicles) return;
    for (let i = row.vehicles.length - 1; i >= 0; i--) {
      const v = row.vehicles[i];
      const vr = v.getHitRect();
      if (
        rect.x < vr.x + vr.w &&
        rect.x + rect.w > vr.x &&
        rect.y < vr.y + vr.h &&
        rect.y + rect.h > vr.y
      ) {
        this._vehicleLayer.removeChild(v.display);
        row.vehicles.splice(i, 1);
        this._pool.release(v);
      }
    }
  }

  /**
   * Get all active vehicles in visible rows (for collision).
   * @param {number} lowRow
   * @param {number} highRow
   * @returns {FroggerVehicle[]}
   */
  getVisibleVehicles(lowRow, highRow) {
    const result = [];
    for (let r = Math.max(0, lowRow); r <= highRow; r++) {
      const row = this._rows.get(r);
      if (row?.vehicles) {
        for (const v of row.vehicles) {
          if (v.active) result.push(v);
        }
      }
    }
    return result;
  }

  /**
   * Remove rows that are no longer visible.
   * @param {number} lowRow — lowest visible row
   * @param {number} highRow — highest visible row
   */
  cleanupOutside(lowRow, highRow) {
    // Also nuke anything past the goal row — defensive in case a previous
    // build (or a stale cache entry) left rows above the museum.
    if (Number.isFinite(this._goalRow)) {
      highRow = Math.min(highRow, this._goalRow);
    }
    for (const [r, row] of this._rows) {
      if (r >= lowRow && r <= highRow) continue;

      if (row.vehicles) {
        for (const v of row.vehicles) {
          this._vehicleLayer.removeChild(v.display);
          this._pool.release(v);
        }
        row.vehicles.length = 0;
      }

      if (row.bgGfx) {
        this._bgLayer.removeChild(row.bgGfx);
        row.bgGfx.destroy();
      }

      if (row.overhangSprites?.length) {
        for (const s of row.overhangSprites) {
          this._overhangLayer.removeChild(s);
          s.destroy?.();
        }
        row.overhangSprites.length = 0;
      }

      this._rows.delete(r);
    }
  }

  /**
   * Check if a world row is a safe zone.
   * @param {number} worldRow
   * @returns {boolean}
   */
  isSafeRow(worldRow) {
    const row = this._rows.get(worldRow);
    return row ? row.type === "safe" : false;
  }

  /**
   * Find the nearest safe row at or below the given row.
   * @param {number} fromRow
   * @returns {number}
   */
  findLastSafeRow(fromRow) {
    for (let r = fromRow; r >= 0; r--) {
      if (this.isSafeRow(r)) return r;
    }
    return 0;
  }

  destroy() {
    for (const [, row] of this._rows) {
      if (row.vehicles) {
        for (const v of row.vehicles) {
          this._pool.release(v);
        }
      }
      if (row.bgGfx) row.bgGfx.destroy();
      if (row.overhangSprites?.length) {
        for (const s of row.overhangSprites) s.destroy?.();
        row.overhangSprites.length = 0;
      }
    }
    this._rows.clear();
    this._rowTypeCache.clear();
    this._pool.releaseAll();
    this.container.removeChildren();
  }

  // ── row generation ─────────────────────────────────

  _generateRow(worldRow) {
    let type;

    if (this._rowTypeCache.has(worldRow)) {
      type = this._rowTypeCache.get(worldRow);
    } else if (worldRow >= this._goalRow) {
      // The row directly beneath the museum entrance is the WIN row. It
      // sits BEHIND the building's lawn (the museum sprite mask extends
      // exactly 1 cell below the building's base), so the player walks
      // "into" the museum to win and never visually stands on a free
      // safe strip at the building's base.
      type = "safe";
    } else if (worldRow === this._goalRow - 1) {
      // First VISIBLE lane below the museum — a ROAD lane right at the
      // building's base. This is the climactic "cross the street to
      // reach the museum" row in front of the finish.
      type = "road";
    } else if (worldRow === this._goalRow - 2) {
      // Second VISIBLE lane below the museum — a calm SAFE strip the
      // player can rest on before tackling the final road. Reset the
      // safe-interval so the natural alternation continues cleanly
      // below this point.
      type = "safe";
      this._nextSafeIn = this._randInt(
        SAFE_INTERVAL_RANGE[0],
        SAFE_INTERVAL_RANGE[1],
      );
    } else if (worldRow < START_SAFE_ROWS) {
      type = "safe";
      this._nextSafeIn = this._randInt(
        SAFE_INTERVAL_RANGE[0],
        SAFE_INTERVAL_RANGE[1],
      );
    } else {
      this._nextSafeIn--;
      if (this._nextSafeIn <= 0) {
        type = "safe";
        this._nextSafeIn = this._randInt(
          SAFE_INTERVAL_RANGE[0],
          SAFE_INTERVAL_RANGE[1],
        );
      } else {
        type = "road";
      }
    }

    this._rowTypeCache.set(worldRow, type);

    const drawOutputs = { overhangSprites: [] };
    const rowData = {
      type,
      bgGfx: this._drawRowBg(worldRow, type, drawOutputs),
      overhangSprites: drawOutputs.overhangSprites,
      laneDef: null,
      spawnTimer: 0,
      vehicles: [],
      lastVehicleType: null,
    };

    if (type === "road" && worldRow < this._goalRow) {
      rowData.laneDef = this._randomLaneDef();
      this._prePopulateRow(worldRow, rowData);
    }

    this._bgLayer.addChild(rowData.bgGfx);
    this._rows.set(worldRow, rowData);

    if (this._onRowGenerated) this._onRowGenerated(worldRow, type);
  }

  _randomLaneDef() {
    return {
      speed: this._randRange(SPEED_RANGE[0], SPEED_RANGE[1]),
      direction: Math.random() < 0.5 ? 1 : -1,
      spawnRate: Math.round(
        this._randRange(SPAWN_RATE_RANGE[0], SPAWN_RATE_RANGE[1]),
      ),
    };
  }

  _prePopulateRow(worldRow, rowData) {
    const laneDef = rowData.laneDef;
    const speedMul =
      (1 + Math.floor(worldRow / 10) * SPEED_INCREASE_PER_10_ROWS) *
      this._speedMultiplier;
    const minCount = Math.max(
      1,
      Math.round(INITIAL_VEHICLES_RANGE[0] * this._density.initialMul),
    );
    const maxCount = Math.max(
      minCount,
      Math.round(INITIAL_VEHICLES_RANGE[1] * this._density.initialMul),
    );
    const count = this._randInt(minCount, maxCount);
    const maxVehicleW = Math.max(...VEHICLE_TYPES.map((type) => type.w));

    const positions = this._randomPositions(
      count,
      GRID_W,
      maxVehicleW,
      MIN_VEHICLE_GAP * this._density.gapMul,
    );

    const exLeft = this._playerExcludeX - this._originX;
    const exRight = exLeft + this._playerExcludeW;

    for (const px of positions) {
      const spec = this._pickVehicleSpec(rowData.lastVehicleType);
      if (px + spec.w > exLeft && px < exRight) continue;
      const rowY = -worldRow * CELL_SIZE + (CELL_SIZE - spec.h) / 2;
      const v = this._pool.get();
      v.configure({
        vehicleType: spec.type,
        x: this._originX + px,
        y: rowY,
        w: spec.w,
        h: spec.h,
        speed: laneDef.speed * speedMul,
        direction: laneDef.direction,
        worldRow,
      });
      this._vehicleLayer.addChild(v.display);
      rowData.vehicles.push(v);
      rowData.lastVehicleType = spec.type;
    }
  }

  // ── spawning ───────────────────────────────────────

  _spawnVehicle(worldRow, laneDef, speedMul) {
    const rowData = this._rows.get(worldRow);
    if (!rowData) return;

    const spec = this._pickVehicleSpec(rowData.lastVehicleType);
    const v = this._pool.get();
    const rowY = -worldRow * CELL_SIZE + (CELL_SIZE - spec.h) / 2;
    const startX =
      laneDef.direction > 0
        ? this._originX - spec.w - 20
        : this._originX + GRID_W + 20;

    v.configure({
      vehicleType: spec.type,
      x: startX,
      y: rowY,
      w: spec.w,
      h: spec.h,
      speed: laneDef.speed * speedMul,
      direction: laneDef.direction,
      worldRow,
    });

    this._vehicleLayer.addChild(v.display);
    rowData.vehicles.push(v);
    rowData.lastVehicleType = spec.type;
  }

  // ── drawing ────────────────────────────────────────

  _drawRowBg(worldRow, type, outputs = { overhangSprites: [] }) {
    const row = new Container();
    const y = -worldRow * CELL_SIZE;
    const isStartRow =
      type === "safe" && worldRow >= 0 && worldRow < START_SAFE_ROWS;
    /** Goal row: clean safe strip directly under the museum, no decorations. */
    const isGoalRow = worldRow >= this._goalRow;

    // Layer 1 (back): lane background — sprite if loaded, procedural fill otherwise.
    //
    // A road-blue underlay is always painted first (except for the transparent
    // start-safe rows, where the bottom strip image provides the visuals).
    // This guarantees that any transparent pixels in a user-supplied lane
    // texture (e.g. grass-edge alpha in `frogger.lane.safe`) blend into the
    // road colour next to it instead of showing the sky-blue theme background
    // bleeding through.
    const bgGfx = new Graphics();
    const laneSprite = !isStartRow
      ? this._themeManager?.createSprite(
          type === "road" ? "frogger.lane.road" : "frogger.lane.safe",
          GRID_W,
          CELL_SIZE,
          { anchorX: 0, anchorY: 0, fitMode: "cover" },
        )
      : null;

    if (!isStartRow) {
      // Background spans the FULL screen width (not just the playable grid),
      // so the left/right margins outside the grid still look like road
      // instead of letting the sky gradient bleed through. The lane sprite /
      // markings / decorations on top stay constrained to the grid below.
      //
      // ALPHA TUNING — lane backgrounds are now ALWAYS fully opaque, even
      // when a custom `frogger.background` is loaded. Earlier builds used
      // alpha 0.84/0.92 to let the backdrop bleed through and provide
      // atmosphere, but that revealed the start-area grass strip and the
      // canvas behind any scrolling lane and looked broken. The
      // atmospheric tint is now provided by an overlay copy of the same
      // background image, applied ON TOP of the world layer at 30 %
      // alpha with a multiply blend (see `FroggerScene._createThemeBackground`).
      const bgX = 0;
      const bgW = this._screenWidth;
      if (laneSprite) {
        bgGfx.rect(bgX, y, bgW, CELL_SIZE);
        bgGfx.fill({ color: COLOR_ROAD, alpha: 1 });
      } else {
        bgGfx.rect(bgX, y, bgW, CELL_SIZE);
        bgGfx.fill({
          color: type === "safe" ? COLOR_SAFE_ZONE : COLOR_ROAD,
          alpha: 1,
        });
      }
    }
    row.addChild(bgGfx);

    if (laneSprite) {
      laneSprite.x = this._originX;
      laneSprite.y = y;
      row.addChild(laneSprite);
    }

    // Layer 2 (front): markings (road) or decor (safe). Both go on top of bg.
    const overlayGfx = new Graphics();
    if (type === "road") {
      const markingSprite = this._themeManager?.createSprite(
        "frogger.lane.marking",
        GRID_W,
        CELL_SIZE,
        { anchorX: 0, anchorY: 0, fitMode: "cover" },
      );
      if (markingSprite) {
        markingSprite.x = this._originX;
        markingSprite.y = y;
        markingSprite.alpha = 0.95;
        row.addChild(markingSprite);
      } else if (!laneSprite) {
        for (let c = 0; c < COLS; c++) {
          overlayGfx.rect(
            this._originX + c * CELL_SIZE + 10,
            y - 1,
            CELL_SIZE - 20,
            2,
          );
          overlayGfx.fill({ color: COLOR_LANE_LINE, alpha: 0.75 });
        }
      }
    } else if (!isStartRow && !isGoalRow) {
      this._drawSafeDecor(row, overlayGfx, worldRow, y, outputs);
    }
    row.addChild(overlayGfx);

    return row;
  }

  _drawSafeDecor(row, g, worldRow, y, outputs) {
    const range = SAFE_DECOR_COUNT_MAX - SAFE_DECOR_COUNT_MIN + 1;
    const count = SAFE_DECOR_COUNT_MIN + Math.abs((worldRow * 37) % range);
    const usedCols = new Set();
    const kindCounts = Object.create(null);

    for (let i = 0; i < count; i++) {
      const seed = Math.abs(worldRow * 97 + i * 53);

      let col = 1 + (seed % Math.max(1, COLS - 2));
      for (let attempt = 0; attempt < 6 && usedCols.has(col); attempt++) {
        col = 1 + ((seed + attempt * 13) % Math.max(1, COLS - 2));
      }
      if (usedCols.has(col)) continue;
      usedCols.add(col);

      const kind = this._pickSafeDecorKind(seed, kindCounts);
      if (!kind) continue;
      kindCounts[kind.id] = (kindCounts[kind.id] ?? 0) + 1;

      const isTall =
        SAFE_DECOR_TALL_KINDS.has(kind.id) &&
        ((seed * 11) % 100) / 100 < SAFE_DECOR_TALL_PROB;
      const sizeMul = isTall
        ? SAFE_DECOR_TALL_MUL[0] +
          (((seed * 17) % 100) / 100) *
            (SAFE_DECOR_TALL_MUL[1] - SAFE_DECOR_TALL_MUL[0])
        : 1;

      const decorW = CELL_SIZE * kind.w * sizeMul;
      const decorH = CELL_SIZE * kind.h * sizeMul;
      const cx = this._originX + col * CELL_SIZE + CELL_SIZE / 2;
      const baseY = y + CELL_SIZE * SAFE_DECOR_BASE_FRACTION;
      const centerY = baseY - decorH / 2;

      const sprite = this._themeManager?.createSprite(
        kind.slot,
        decorW,
        decorH,
      );
      if (sprite) {
        sprite.x = cx;
        sprite.y = centerY;
        if (isTall && outputs?.overhangSprites) {
          this._overhangLayer.addChild(sprite);
          outputs.overhangSprites.push(sprite);
        } else {
          row.addChild(sprite);
        }
        continue;
      }

      if (!kind.fallback) continue;
      const decorX = cx - decorW / 2;
      const decorY = baseY - decorH;
      if (kind.fallback === "tree") {
        drawTree(g, decorX, decorY, decorW, decorH);
      } else {
        drawBush(g, decorX, decorY, decorW, decorH, { seed });
      }
    }
  }

  /**
   * Pick a decoration kind, weighted, but respecting the per-row caps in
   * SAFE_DECOR_MAX_PER_ROW. If the initial roll hits a capped kind, we
   * re-roll from a reduced pool until we find a legal one (or give up).
   */
  _pickSafeDecorKind(seed, kindCounts) {
    for (let attempt = 0; attempt < 8; attempt++) {
      const r = (seed + attempt * 211) % SAFE_DECOR_WEIGHT_SUM;
      let acc = 0;
      for (const k of SAFE_DECOR_KINDS) {
        acc += k.weight;
        if (r < acc) {
          const cap = SAFE_DECOR_MAX_PER_ROW[k.id];
          if (cap != null && (kindCounts[k.id] ?? 0) >= cap) break;
          return k;
        }
      }
    }
    return SAFE_DECOR_KINDS[0];
  }

  _tryLaneChange(vehicle, currentRow, currentRowData) {
    const dir = Math.random() < 0.5 ? 1 : -1;
    const targetRow = currentRow + dir;
    const target = this._rows.get(targetRow);
    if (!target || target.type !== "road" || !target.laneDef) return;
    if (target.laneDef.direction !== currentRowData.laneDef.direction) return;
    if (target.vehicles && target.vehicles.length >= MAX_VEHICLES_PER_ROW)
      return;

    if (this._wouldOverlapInRow(vehicle, target)) return;

    const idx = currentRowData.vehicles.indexOf(vehicle);
    if (idx !== -1) currentRowData.vehicles.splice(idx, 1);
    if (!target.vehicles) target.vehicles = [];
    target.vehicles.push(vehicle);
    vehicle.startDrift(targetRow);
  }

  /**
   * Push apart any vehicles on the same row whose hitboxes overlap.
   * Trailing vehicle gets nudged behind the one ahead of it.
   */
  _resolveOverlaps(vehicles) {
    if (!vehicles || vehicles.length < 2) return;
    for (let i = 0; i < vehicles.length; i++) {
      const a = vehicles[i];
      for (let j = i + 1; j < vehicles.length; j++) {
        const b = vehicles[j];
        const ax = a.x,
          aw = a.hitbox.width;
        const bx = b.x,
          bw = b.hitbox.width;
        const overlap = Math.min(ax + aw, bx + bw) - Math.max(ax, bx);
        if (overlap > 0) {
          const push = overlap / 2 + 2;
          if (ax < bx) {
            a.x -= push;
            b.x += push;
          } else {
            a.x += push;
            b.x -= push;
          }
          a.syncPosition();
          b.syncPosition();
        }
      }
    }
  }

  /**
   * Check if placing a vehicle in a row would cause an X-axis overlap
   * with any existing vehicle in that row.
   */
  _wouldOverlapInRow(vehicle, rowData) {
    if (!rowData.vehicles || rowData.vehicles.length === 0) return false;
    const vx = vehicle.x;
    const vw = vehicle.hitbox.width;
    const gap = MIN_VEHICLE_GAP * this._density.gapMul;
    for (const other of rowData.vehicles) {
      if (other === vehicle) continue;
      const ox = other.x;
      const ow = other.hitbox.width;
      if (vx < ox + ow + gap && vx + vw + gap > ox) {
        return true;
      }
    }
    return false;
  }

  // ── util ───────────────────────────────────────────

  _randomPositions(count, laneWidth, vehicleW, minGap) {
    const positions = [];
    for (let i = 0; i < count; i++) {
      let placed = false;
      for (let attempt = 0; attempt < 60; attempt++) {
        const px = Math.random() * (laneWidth - vehicleW);
        let valid = true;
        for (const ex of positions) {
          if (Math.abs(px - ex) < vehicleW + minGap) {
            valid = false;
            break;
          }
        }
        if (valid) {
          positions.push(px);
          placed = true;
          break;
        }
      }
      if (!placed) continue;
    }
    return positions;
  }

  _pickVehicleSpec(previousType = null) {
    let spec = VEHICLE_TYPES[this._randInt(0, VEHICLE_TYPES.length - 1)];
    let attempts = 0;
    while (
      VEHICLE_TYPES.length > 1 &&
      spec.type === previousType &&
      attempts < 6
    ) {
      spec = VEHICLE_TYPES[this._randInt(0, VEHICLE_TYPES.length - 1)];
      attempts++;
    }
    return spec;
  }

  _randRange(min, max) {
    return min + Math.random() * (max - min);
  }
  _randInt(min, max) {
    return min + Math.floor(Math.random() * (max - min + 1));
  }
}
